import { StyleSheet, css } from 'aphrodite';
import { Avatar } from '../../../web/components-v2/Avatar';
import { Button } from '../../../web/components-v2/Button';
import { Skeleton } from '../../../web/components-v2/Skeleton';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';
import { ChevronLeft } from '../../../web/components/svgs/icons/ChevronLeft';
import { useTimeEntryDayQuery } from '../../queries/TimeEntry';
import { HoursEntryForm } from '../../timeTracking/components/HoursEntryForm';
import { LoadError } from '../../timeTracking/components/LoadError';
import { TimeEntryHistory } from '../../timeTracking/components/TimeEntryHistory';
import { TimeEntryStatusPill } from '../../timeTracking/components/TimeEntryStatusPill';
import { formatDateTime, formatDayLong } from '../../timeTracking/days';
import { formatHours } from '../../timeTracking/hours';
import { lockedDayMessage } from '../../timeTracking/lockedDay';
import { timeTrackingStyles as tt } from '../../timeTracking/styles';
import { useUserNames } from '../../timeTracking/useUserNames';

export function TimesheetDay({
	day,
	canActAsSelf,
	onBack,
}: {
	day: string;
	canActAsSelf: boolean;
	onBack: () => void;
}) {
	const dayQuery = useTimeEntryDayQuery({ day });
	const { nameFor, initialsFor } = useUserNames();
	const data = dayQuery.data;
	const entry = data?.entry;

	return (
		<>
			<div className={css(styles.header)}>
				<Button type='button' variant='link' size='sm' styles={[styles.back]} onClick={onBack}>
					<ChevronLeft className={css(styles.backIcon)} />
					All days
				</Button>
				<div className={css(styles.titleRow)}>
					<h1 className={css(tt.pageTitle)}>{formatDayLong(day)}</h1>
					{data ? <TimeEntryStatusPill entry={entry} block={data.block} /> : null}
				</div>
			</div>

			{dayQuery.isLoading ? (
				<Skeleton styles={[styles.skeleton]} />
			) : dayQuery.isError || !data ? (
				<div className={css(tt.card)}>
					<LoadError message='We couldn’t load this day.' onRetry={() => dayQuery.refetch()} />
				</div>
			) : (
				<div className={css(styles.columns)}>
					<div className={css(styles.main)}>
						{entry?.status === 'Rejected' && entry.reviewComment ? (
							<div className={css(tt.card, tt.cardBody, styles.comment)}>
								<Avatar
									fallbackText={initialsFor(entry.reviewedByUserId)}
									altText={nameFor(entry.reviewedByUserId)}
									smallText
									styles={[styles.avatar]}
								/>
								<div className={css(styles.commentText)}>
									<span className={css(tt.muted)}>
										<strong className={css(styles.commentAuthor)}>{nameFor(entry.reviewedByUserId)}</strong>{' '}
										{entry.lastApprovedAt ? 'reopened this day' : 'sent this day back'} ·{' '}
										{formatDateTime(entry.reviewedAt)}
									</span>
									<span className={css(styles.commentBody)}>{entry.reviewComment}</span>
								</div>
							</div>
						) : null}

						{entry?.exceptionReason ? (
							<p className={css(tt.muted)}>
								{nameFor(entry.exceptionOpenedByUserId)} opened this day: “{entry.exceptionReason}”
							</p>
						) : null}

						<section className={css(tt.card, tt.cardBody)} aria-labelledby='timesheet-day-form-heading'>
							<h2 id='timesheet-day-form-heading' className={css(tt.cardTitle)}>
								{entry?.status === 'Rejected' ? 'Revise your hours' : 'Your hours'}
							</h2>
							<DayBody
								data={data}
								canActAsSelf={canActAsSelf}
								onSubmitted={onBack}
								approverName={nameFor(entry?.reviewedByUserId)}
							/>
						</section>
					</div>

					{entry ? (
						<section className={css(tt.card, tt.cardBody, styles.history)} aria-labelledby='timesheet-history-heading'>
							<h2 id='timesheet-history-heading' className={css(tt.cardTitle)}>
								History
							</h2>
							<TimeEntryHistory entryId={entry.id} nameFor={nameFor} />
						</section>
					) : null}
				</div>
			)}
		</>
	);
}

function DayBody({
	data,
	canActAsSelf,
	onSubmitted,
	approverName,
}: {
	data: NonNullable<ReturnType<typeof useTimeEntryDayQuery>['data']>;
	canActAsSelf: boolean;
	onSubmitted: () => void;
	approverName: string;
}) {
	const { entry } = data;
	if (!data.isRequired) {
		return <p className={css(tt.muted)}>No hours are needed for this day.</p>;
	}
	if (data.canEdit) {
		return (
			<>
				{entry?.hours !== undefined && entry.status !== 'Draft' ? (
					<p className={css(tt.muted)}>
						You submitted {formatHours(entry.hours)} hours. Change the total if needed, then attest again to resubmit.
					</p>
				) : null}
				<HoursEntryForm
					key={`${data.day}-${entry?.status}-${entry?.version}`}
					day={data.day}
					entry={entry}
					canActAsSelf={canActAsSelf}
					onSubmitted={onSubmitted}
				/>
				{data.warnings.map(warning => (
					<p key={warning} className={css(tt.muted)}>
						{warning}
					</p>
				))}
			</>
		);
	}
	return (
		<p className={css(tt.muted)}>
			{lockedDayMessage(data, ` by ${approverName} on ${formatDateTime(entry?.reviewedAt)}`)}
		</p>
	);
}

const styles = StyleSheet.create({
	avatar: {
		flexShrink: 0,
		height: 32,
		width: 32,
	},
	back: {
		alignSelf: 'flex-start',
		gap: SPACE[4],
		paddingLeft: 0,
	},
	backIcon: {
		height: 16,
		width: 16,
	},
	columns: {
		alignItems: 'flex-start',
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[20],
	},
	comment: {
		flexDirection: 'row',
		gap: SPACE[12],
	},
	commentAuthor: {
		color: THEME.colors.text.main,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	commentBody: {
		fontSize: THEME.typography.fontSizes.md,
		lineHeight: '24px',
	},
	commentText: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
	},
	header: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[8],
	},
	history: {
		flex: '0 1 380px',
	},
	main: {
		display: 'flex',
		flex: '1 1 520px',
		flexDirection: 'column',
		gap: SPACE[20],
		minWidth: 0,
	},
	skeleton: {
		height: 200,
		width: '100%',
	},
	titleRow: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[12],
	},
});
